<script setup lang="ts">
  import { computed, ref } from 'vue';
  import Resizer from './resizer.vue';
  import { SplitPaneProps } from './split-pane';

  defineOptions({
    name: 'ISplitPane',
  });

  const props = defineProps(SplitPaneProps);

  const emit = defineEmits(['resize']);

  const active = ref(false);
  const hasMoved = ref(false);
  const percent = ref(props.splitSet?.defaultPercent);
  const type = computed(() => (props.splitSet?.split === 'vertical' ? 'width' : 'height'));
  const resizeType = computed(() => (props.splitSet?.split === 'vertical' ? 'left' : 'top'));

  const leftClass = computed(() => ['i-splitter-pane i-splitter-paneL', props.splitSet?.split]);

  const rightClass = computed(() => ['i-splitter-pane i-splitter-paneR', props.splitSet?.split]);

  const cursor = computed(() => {
    return active.value
      ? props.splitSet?.split === 'vertical'
        ? { cursor: 'col-resize' }
        : { cursor: 'row-resize' }
      : { cursor: 'default' };
  });

  const onClick = () => {
    if (!hasMoved.value) {
      percent.value = 50;
      emit('resize', percent.value);
    }
  };

  const onMouseDown = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const className = target.className;
    if (className.includes('toLeft')) {
      if (percent.value === 100) {
        percent.value = props.splitSet?.defaultPercent;
      } else {
        percent.value = 0;
      }
      emit('resize', percent.value);
    } else if (className.includes('toRight')) {
      if (percent.value === 0) {
        percent.value = props.splitSet?.defaultPercent;
      } else {
        percent.value = 100;
      }
      emit('resize', percent.value);
    } else {
      active.value = true;
      hasMoved.value = false;
    }
  };

  const onMouseUp = (): void => {
    active.value = false;
  };

  const onMouseMove = (e: MouseEvent): void => {
    if (e.buttons === 0 || e.which === 0) {
      active.value = false;
    }

    if (active.value) {
      let offset = 0;
      let target = e.currentTarget as HTMLElement;
      if (props.splitSet?.split === 'vertical') {
        while (target) {
          offset += target.offsetLeft;
          target = target.offsetParent as HTMLElement;
        }
      } else {
        while (target) {
          offset += target.offsetTop;
          target = target.offsetParent as HTMLElement;
        }
      }

      const currentPage = props.splitSet?.split === 'vertical' ? e.pageX : e.pageY;
      // @ts-ignore
      const offsetWidth = e.currentTarget?.offsetWidth || 0;
      // @ts-ignore
      const offsetHeight = e.currentTarget?.offsetHeight || 0;
      const targetOffset = props.splitSet?.split === 'vertical' ? offsetWidth : offsetHeight;
      const percents = Math.floor(((currentPage - offset) / targetOffset) * 10000) / 100;

      if (percents > props.splitSet?.minPercent && percents < 100 - props.splitSet?.minPercent) {
        percent.value = percents;
      }

      emit('resize', percent.value);

      hasMoved.value = true;
    }
  };
</script>

<template>
  <div class="vue-splitter-container clearfix" :style="cursor" @mouseup="onMouseUp" @mousemove="onMouseMove">
    <div
      :class="leftClass"
      :style="`${type}: ${percent === 100 ? 'calc(100% - 10px)' : `${percent}%`}; display: ${percent === 0 ? 'none' : ''}`"
    >
      <slot name="paneL" />
    </div>
    <Resizer
      :style="`${resizeType}:${percent}%; transform: ${percent === 100 ? 'translateX(-10px)' : ''}`"
      :split="splitSet?.split"
      @mousedown="onMouseDown"
      @click="onClick"
    />
    <div :class="rightClass" :style="`${type}: ${100 - percent}%; display: ${percent === 100 ? 'none' : ''}`">
      <slot name="paneR" />
    </div>
    <div v-show="active" class="vue-splitter-container-mask" />
  </div>
</template>
